import styled from "@emotion/styled"
import type { BreakpointValues } from "@porsche-design-system/components-react"
import { breakpoint } from "@porsche-design-system/components-react/emotion"
import type { ResponsiveImageVariantView, ResponsiveImageView } from "@slfinrtl/ui-bff-routes"
import { splitByMimeType } from "src/common/splitByMimeType"
import type { PictureProps } from "../picture/Picture"
import { Picture } from "../picture/Picture"

declare type BreakpointCustomizable<T> = T | BreakpointValues<T>

type PictureSize = "full" | "three-quarters" | "two-thirds" | "half" | "one-third" | "one-quarter" | "one-eighth"

interface PictureResponsiveProps extends Partial<PictureProps> {
	/**
	 * The vehicle image containing the responsive image variants that should be displayed.
	 */
	picture?: ResponsiveImageView

	/**
	 * The approximate width of the image relative to the viewport width.
	 * If the image goes over the entire screen width, choose "full".
	 * If the image goes over a quarter of the screen, choose "quarter".
	 * This can be configured globally or per breakpoint.
	 */
	sizes: BreakpointCustomizable<PictureSize>
}

export const PictureResponsive = (props: PictureResponsiveProps) => {
	const { aspectRatio = "4/3", picture, sizes, ...rest } = props

	if (!picture) {
		return <Placeholder aspectRatio={aspectRatio} {...rest} />
	}

	const { jpeg, webp } = splitByMimeType(picture.variants)

	return (
		<Picture aspectRatio={aspectRatio ?? picture.aspectRatio} {...rest}>
			{webp.length > 0 && <source type="image/webp" srcSet={buildSrcSet(webp)} sizes={mapSizes(sizes)} />}
			<source type="image/jpeg" srcSet={buildSrcSet(jpeg)} sizes={mapSizes(sizes)} />
		</Picture>
	)
}

const buildSrcSet = (variants: ResponsiveImageVariantView[]): string | undefined => {
	if (variants.length === 0) {
		return undefined
	}

	return variants.map((variant) => `${variant.url} ${variant.width}w`).join(", ")
}

const mapPictureSize = (pictureSize: PictureSize): string => {
	switch (pictureSize) {
		case "one-eighth":
			return "12.5vw"
		case "one-quarter":
			return "25vw"
		case "one-third":
			return "33.3vw"
		case "half":
			return "50vw"
		case "two-thirds":
			return "66.6vw"
		case "three-quarters":
			return "75vw"
		case "full":
			return "100vw"
	}
}

const mapSizes = (sizes: BreakpointCustomizable<PictureSize>): string =>
	typeof sizes === "string"
		? mapPictureSize(sizes)
		: Object.entries(sizes)
				.reduce<string[]>((queries, [key, value]) => {
					if (key === "base") {
						return [...queries, mapPictureSize(value)]
					}
					const brk = breakpoint[key as keyof BreakpointValues<PictureSize>]
					return [...queries, `(min-width: ${brk}px) ${mapPictureSize(value)}`]
				}, [])
				.sort()
				.join(", ")

const Placeholder = styled(Picture)`
	text-indent: 100%;
	white-space: nowrap;
	overflow: hidden;
`
